import api from './api';

export interface RegiaoItem {
  estado: string;   
  municipio: string; 
  municipioNormalizado: string;
}

const estadosMap: Record<string, string> = {
  AC: "Acre",
  AL: "Alagoas",
  AP: "Amapá",
  AM: "Amazonas",
  BA: "Bahia",
  CE: "Ceará",
  DF: "Distrito Federal",
  ES: "Espírito Santo",
  GO: "Goiás",
  MA: "Maranhão",
  MT: "Mato Grosso",
  MS: "Mato Grosso do Sul",
  MG: "Minas Gerais",
  PA: "Pará",
  PB: "Paraíba",
  PR: "Paraná",
  PE: "Pernambuco",
  PI: "Piauí",
  RJ: "Rio de Janeiro",
  RN: "Rio Grande do Norte",
  RS: "Rio Grande do Sul",
  RO: "Rondônia",
  RR: "Roraima",
  SC: "Santa Catarina",
  SP: "São Paulo",
  SE: "Sergipe",
  TO: "Tocantins",
};

// Tipagem simplificada dos dados detalhados de município
export interface MunicipioResponse {
  municipio: string;
  habitantes: number;
  homens: number;
  mulheres: number;
  casados: number;
  area: number;
  densidade_pop: number;
  densidade_dom: number;
  pib: number;
  pib_per_capita: number;
  idh: string;
  pop_empregada: number;
  pop_economicamente_ativa: number;
  domicilios: number;
  apartamentos: number;
  alugados: number;
  em_favela: number;
  pessoas_por_domicilio: number;
  dom_1_pessoa: number;
  dom_2_pessoas: number;
  dom_3_pessoas: number;
  dom_4_pessoas: number;
  dom_5mais_pessoas: number;
  media_per_capita: number;
  media_por_domicilio: number;
  media_mensal_classe_a: number;
  media_mensal_a1: number;
  media_mensal_a2: number;
  media_mensal_classe_b: number | null;
  media_mensal_b1: number;
  media_mensal_b2: number;
  media_mensal_classe_c: number;
  media_mensal_c1: number;
  media_mensal_c2: number;
  media_mensal_classe_de: number;
  media_mensal_d: number;
  media_mensal_e: number;
  domicilios_classe_a: number;
  domicilios_a1: number;
  domicilios_a2: number;
  domicilios_classe_b: number;
  domicilios_b1: number;
  domicilios_b2: number;
  domicilios_classe_c: number;
  domicilios_c1: number;
  domicilios_c2: number;
  domicilios_classe_de: number;
  domicilios_d: number;
  domicilios_e: number;
  classe_a: number;
  a1: number;
  a2: number;
  classe_b: number;
  b1: number;
  b2: number;
  classe_c: number;
  c1: number;
  c2: number;
  classe_de: number;
  d: number;
  e: number;
  criancas_0_4: number;
  criancas_5_9: number;
  criancas_10_14: number;
  crianca_total: number;
  jovens_15_19: number;
  jovens_20_24: number;
  jovens_25_29: number;
  jovens_total: number;
  adultos_30_39: number;
  adultos_40_49: number;
  adultos_50_59: number;
  adultos_total: number;
  idosos_60_64: number;
  idosos_65_69: number;
  idosos_70mais: number;
  idosos_total: number;
  dom_com_automovel: number;
  dom_com_moto: number;
  dom_com_internet: number;
  dom_com_celular: number;
  cinco_minutos: number, 
  seis_a_quinze_minutos: number, 
  quinze_a_trinta_minutos: number, 
  trinta_minutos_a_uma_hora: number, 
  uma_a_duas_horas: number, 
  duas_a_quatro_horas: number, 
  mais_de_quatro_horas: number,
}


class SocioeconomicoService {
  private transformarMunicipios(lista: any[]): RegiaoItem[] {
    return lista.map((item) => {
      // item = { municipio: "abadia de goias", nome_nao_normalizado: "Abadia de Goiás - GO", uf: "GO" }
      const partes = item.nome_nao_normalizado.split("-");
      const municipioNome = partes[0].trim();
      const uf = item.uf.trim();

      return {
        municipio: municipioNome,                       // exibição no filtro
        estado: estadosMap[uf as keyof typeof estadosMap] || uf, // nome completo
        municipioNormalizado: item.municipio.trim(),    // usado nos GETs
      };
    });
  }

  async getMunicipios(): Promise<RegiaoItem[]> {
    try {
      const response = await api.get<any[]>("/nome-do-municipio/");
      return this.transformarMunicipios(response.data);
    } catch (error) {
      console.error("❌ Erro ao buscar municípios:", error);
      throw error;
    }
  }

  async getDadosMunicipio(municipioItem: RegiaoItem): Promise<MunicipioResponse> {
    try {
      const municipioEncoded = encodeURIComponent(municipioItem.municipioNormalizado);
      const response = await api.get<MunicipioResponse>(`/municipios-socioeconomico/${municipioEncoded}/`);
      return response.data;
    } catch (error) {
      console.error(`❌ Erro ao buscar dados do município ${municipioItem.municipio}:`, error);
      throw error;
    }
  }


  async getBairro(municipioItem: RegiaoItem): Promise<any> {
    try {
      // Normaliza o nome do município (sem afetar estado/sigla)
      const municipioNome = municipioItem.municipio
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .toLowerCase()
        .trim();

      const municipioEncoded = encodeURIComponent(municipioNome);
      const response = await api.get(`/bairros?municipio=${municipioEncoded}`);

      const features = response.data['bairros'].map((bairro: any) => {
        let geometry = null;
        try {
          geometry = JSON.parse(bairro.geom);
        } catch (e) {
          console.error("Erro ao parsear geom:", e);
        }
        return {
          type: "Feature",
          geometry,
          properties: {
            id: bairro.id,
            nome: bairro.nome,
            municipio: bairro.municipio,
            uf: bairro.uf,
            tipo: bairro.tipo,
            regiao: bairro.regiao,
            cd_bairro: bairro.cd_bairro,
            cd_setor: bairro.cd_setor,
            area_km2: bairro.area_km2,
            densidade_demografica: bairro.densidade_demografica,
          },
        };
      });

      // Adiciona o centro do município
      const centroGeral = response.data['centro_geral']; // { latitude, longitude }

      return {
        type: "FeatureCollection",
        features,
        centroGeral,
      };
    } catch (error) {
      console.error(
        `❌ Erro ao buscar mapa de bairros do município ${municipioItem.municipio}:`,
        error
      );
      throw error;
    }
  }

  async getMunicipiosMapa(municipioItem: RegiaoItem): Promise<any> {
    try {
      // Normaliza o nome do município
      const municipioNome = municipioItem.municipio
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9\s-]/g, "")
        .toLowerCase()
        .trim();

      const municipioEncoded = encodeURIComponent(municipioNome);
      const response = await api.get(`/municipio-mapa?nome=${municipioEncoded}`);

      const features = response.data["municipios"].map((mun: any) => {
        let geometry = null;
        try {
          geometry = JSON.parse(mun.geom);
        } catch (e) {
          console.error("Erro ao parsear geom:", e);
        }
        return {
          type: "Feature",
          geometry,
          properties: {
            id: mun.id,
            nome: mun.nome,
            uf: mun.uf,
            densidade_demografica: mun.densidade_demografica,
          },
        };
      });

      const centroGeral = response.data["centro_geral"];

      return {
        type: "FeatureCollection",
        features,
        centroGeral,
      };
    } catch (error) {
      console.error(
        `❌ Erro ao buscar mapa de municípios do município ${municipioItem.municipio}:`,
        error
      );
      throw error;
    }
  }

  async getSetores(municipioItem: RegiaoItem): Promise<any> {
    try {
      // Normaliza o nome do município
      const municipioNome = municipioItem.municipio
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9\s-]/g, "")
        .toLowerCase()
        .trim();

      const municipioEncoded = encodeURIComponent(municipioNome);
      const response = await api.get(`/setor?municipio=${municipioEncoded}`);

      const features = response.data["setores"].map((setor: any) => {
        let geometry = null;
        try {
          geometry = JSON.parse(setor.geom);
        } catch (e) {
          console.error("Erro ao parsear geom:", e);
        }
        return {
          type: "Feature",
          geometry,
          properties: {
            municipio: setor.municipio,
            uf: setor.uf,
            regiao: setor.regiao,
            bairro: setor.bairro,
            cd_setor: setor.cd_setor,
            area_km2: setor.area_km2,
            densidade_demografica: setor.densidade_demografica,
          },
        };
      });

      const centroGeral = response.data["centro_geral"];

      return {
        type: "FeatureCollection",
        features,
        centroGeral,
      };
    } catch (error) {
      console.error(
        `❌ Erro ao buscar mapa de setores do município ${municipioItem.municipio}:`,
        error
      );
      throw error;
    }
  }

}

export const socioeconomicoService = new SocioeconomicoService();
